import React, { useMemo } from 'react';
import { Box, Typography, Chip, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { AccountWithBalance } from '../types/api';
import { InstitutionMark } from './InstitutionMark';
import { accountLabel } from '../utils/account-label';
import { formatCurrency } from '../utils/currency';
import { useBaseCurrency } from '../stores/currency-store';
import { useAuthStore } from '../stores/auth-store';

/** Whose accounts to show. */
export type OwnerScope = 'mine' | 'household' | string; // a user id selects one member

interface AccountPickerProps {
  accounts: AccountWithBalance[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  scope: OwnerScope;
  onScopeChange: (s: OwnerScope) => void;
  emptyHint?: string;
}

/**
 * Choose an account, and choose whose accounts are even on offer.
 *
 * Replaces a single flat dropdown. Two things were wrong with that:
 *
 *  1. It defaulted to whichever account sorted first, so a member signing in
 *     landed on their PARTNER'S account and saw an empty page — reading as
 *     "I have no holdings" rather than "you are looking at someone else's
 *     account".
 *  2. It collapsed name, owner and institution into one string, which is why
 *     accounts ended up named things like "Anshuman Biswas ••7214". Owner and
 *     institution are attributes of an account, not part of its name, and once
 *     they are shown as such the name can just be "RRSP".
 *
 * Your own accounts come first and are visually distinct, because that is what
 * a member wants nine times out of ten.
 */
export const AccountPicker: React.FC<AccountPickerProps> = ({
  accounts, selectedId, onSelect, scope, onScopeChange, emptyHint,
}) => {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const baseCurrency = useBaseCurrency();

  /** Distinct owners, so the scope control offers real people rather than ids. */
  const owners = useMemo(() => {
    const map = new Map<string, string>();
    accounts.forEach((a) => {
      if (a.owner_user_id) {
        // Their chosen name, not a mangled email. "tia.guha" beside a
        // shared account reads as a database key rather than a person.
        map.set(a.owner_user_id,
          a.owner_name || a.owner_email?.split('@')[0] || 'Member');
      }
    });
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [accounts]);

  const [showHidden, setShowHidden] = React.useState(false);
  const hiddenCount = useMemo(() => accounts.filter((a) => a.is_hidden).length, [accounts]);

  const visible = useMemo(() => {
    const inScope = accounts.filter((a) => {
      // Hidden accounts are out by default. Presentation only -- they still
      // count toward net worth, so this cannot make money disappear.
      if (a.is_hidden && !showHidden) return false;
      if (scope === 'household') return true;
      if (scope === 'mine') return a.owner_user_id === currentUserId || a.is_joint;
      return a.owner_user_id === scope || a.is_joint;
    });
    // Own accounts first, then joint, then everyone else's — the order a
    // member scans in. Alphabetical within each group so position is stable.
    return inScope.slice().sort((a, b) => {
      const rank = (x: AccountWithBalance) =>
        x.owner_user_id === currentUserId ? 0 : x.is_joint ? 1 : 2;
      const d = rank(a) - rank(b);
      return d !== 0 ? d : accountLabel(a).localeCompare(accountLabel(b));
    });
  }, [accounts, scope, currentUserId, showHidden]);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="overline" color="text.secondary">Showing</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scope}
          onChange={(_, v) => v && onScopeChange(v)}
        >
          <ToggleButton value="mine">Mine</ToggleButton>
          {/* One button per member, so "just Tia's" is a click rather than a
              mental filter over a mixed list. */}
          {owners
            .filter((o) => o.id !== currentUserId)
            .map((o) => (
              <ToggleButton key={o.id} value={o.id} sx={{ textTransform: 'none' }}>
                {o.label}
              </ToggleButton>
            ))}
          <ToggleButton value="household">Whole household</ToggleButton>
        </ToggleButtonGroup>

        {hiddenCount > 0 && (
          <Chip
            size="small"
            variant={showHidden ? 'filled' : 'outlined'}
            onClick={() => setShowHidden((v) => !v)}
            label={showHidden ? `Hiding ${hiddenCount} again` : `Show ${hiddenCount} hidden`}
            sx={{ cursor: 'pointer' }}
          />
        )}
      </Stack>

      {visible.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          {emptyHint ?? 'No accounts to show for this selection.'}
        </Typography>
      ) : (
        <Box
          role="radiogroup"
          aria-label="Select an account"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
            gap: 1.5,
          }}
        >
          {visible.map((a) => {
            const selected = a.id === selectedId;
            const isMine = a.owner_user_id === currentUserId;
            return (
              <Box
                key={a.id}
                role="radio"
                aria-checked={selected}
                tabIndex={0}
                onClick={() => onSelect(a.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(a.id); }}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: selected ? 'primary.main' : 'var(--border-soft)',
                  bgcolor: selected ? 'action.selected' : 'transparent',
                  transition: 'border-color var(--dur-base), background-color var(--dur-base)',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  '&:hover': { borderColor: 'primary.light' },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <InstitutionMark name={a.institution || a.name} size={28} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    {/* The account's own name. Owner and institution live
                        below as attributes, which is what lets this read
                        "RRSP" instead of "Tia Wealthsimple RRSP". */}
                    <Typography
                      variant="body2"
                      fontWeight={650}
                      sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {accountLabel(a)}
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                      {a.institution && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {a.institution}
                        </Typography>
                      )}
                      {a.is_hidden && (
                        <Chip label="Hidden" size="small" variant="outlined" sx={{ height: 18 }} />
                      )}
                      {a.is_joint ? (
                        <Chip label="Joint" size="small" color="secondary" sx={{ height: 18 }} />
                      ) : (
                        <Chip
                          label={isMine ? 'You' : (a.owner_name || a.owner_email?.split('@')[0] || 'Member')}
                          size="small"
                          color={isMine ? 'primary' : 'default'}
                          variant={isMine ? 'filled' : 'outlined'}
                          sx={{ height: 18 }}
                        />
                      )}
                    </Stack>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {formatCurrency(a.current_amount, a.currency, { base: baseCurrency })}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

/**
 * The account a member should land on.
 *
 * Prefers their own, then a joint account, and only then anyone else's. A
 * member opening Holdings and seeing their partner's empty account reads it as
 * "I have no holdings", which is both wrong and discouraging.
 */
export function defaultAccountFor(
  accounts: AccountWithBalance[], currentUserId?: string,
): string | null {
  if (accounts.length === 0) return null;
  const mine = accounts.find((a) => a.owner_user_id === currentUserId);
  if (mine) return mine.id;
  const joint = accounts.find((a) => a.is_joint);
  if (joint) return joint.id;
  return accounts[0].id;
}
